import dayjs from 'dayjs';

/**
 * Create a DateService instance for timezone-aware date operations.
 * @param {string} timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns {import('@dashhudson/dashing-ui').DateService}
 */
export function createDateService(timezone) {
  return {
    get timezone() {
      return timezone;
    },
    now: () => dayjs.tz(undefined, timezone),
    fromUtc: (date) => dayjs.utc(date).tz(timezone),
    toUtc: (date) => dayjs.tz(date, timezone).utc(),
    format: (date, formatStr) => dayjs.tz(date, timezone).format(formatStr),
    formatUtc: (date, formatStr) => dayjs.utc(date).tz(timezone).format(formatStr),
  };
}

export const FAR_FUTURE = new Date(8640000000000000);
export const DISTANT_PAST = new Date(-8640000000000000);
export const ALL_TIME = Object.freeze({ start: DISTANT_PAST, end: FAR_FUTURE });

/**
 * An object that combines two dates to represent the time interval.
 * @typedef {{start: Date, end: Date}} Interval
 */

/**
 * An object that combines two Dayjs objects to represent the time interval.
 * @typedef {{start: Dayjs, end: Dayjs}} DayjsInterval
 */

/**
 * An object that combines a preset and a date range.
 * Corresponds to the `PresetType` defined in dashing-ui
 * @typedef {{value: string, label: string, range: () => DayjsInterval | null}} PresetType
 */

/**
 * An object that combines a preset and a date range.
 * Corresponds to the `DateRangeModel` defined in dashing-ui
 * @typedef {{preset: string, presetObject: PresetType, range: Interval | null}} DateRangeModel
 */

/**
 * Convert date string from a given format to another format.
 * i.e., reformatDateString('2000-03-15', 'YYYY-MM-DD', 'MMMM D, YYYY') = 'March 15, 2000'
 * @param {string} dateString
 *   - The original date string.
 * @param {string} inputDateFormat
 *   - dayjs format code for the original date string.
 * @param {string} targetDateFormat
 *   - dayjs format code for the target output.
 * @response {string}
 *   - Reformatted date string according to the format code.
 * - For more format codes, check: https://day.js.org/docs/en/display/format
 */
export function reformatDateString(dateString, inputDateFormat, targetDateFormat) {
  const parsed = dayjs(dateString, inputDateFormat);
  // Fall back to lenient parsing if strict format fails (e.g. '2021-4-11' with 'YYYY-MM-DD')
  return (parsed.isValid() ? parsed : dayjs(dateString)).format(targetDateFormat);
}

/**
 * Get a merged date range string.
 * @param {string} startDateString - Start date in YYYY-MM-DD format. 2021-01-01
 * @param {string} endDateString - End date in YYYY-MM-DD format.
 * @response {string} i.e., 'Jane 11 - June 12, 2021'
 */
export function getMergedDateRange(startDateString, endDateString) {
  let formattedStart = reformatDateString(startDateString, 'YYYY-MM-DD', 'MMMM D, YYYY');
  const formattedEnd = reformatDateString(endDateString, 'YYYY-MM-DD', 'MMMM D, YYYY');
  // Merge common year if start and end has the same value
  if (formattedStart.split(', ')[1] === formattedEnd.split(', ')[1]) {
    [formattedStart] = formattedStart.split(', ');
  }
  return `${formattedStart} - ${formattedEnd}`;
}

/**
 * Get days interval between two dates.
 * @param {string} startDate - Start date in YYYY-MM-DD format. 2021-01-01
 * @param {string} endDate - End date in YYYY-MM-DD format.
 * @response {int} i.e., 3
 */
export function differenceBetweenDays(startDate, endDate) {
  // Coerce null/undefined to 0 (epoch) to match date-fns behavior where new Date(null) = epoch
  const end = dayjs(endDate ?? 0);
  const start = dayjs(startDate ?? 0);
  return end.startOf('day').diff(start.startOf('day'), 'day');
}

/**
 * Return the formatted date string in the given format.
 * @param {import('dayjs').ConfigType} date
 * @param {string} pattern - dayjs format string (https://day.js.org/docs/en/display/format)
 * @returns {string}
 */
export function formatDate(date, pattern) {
  return dayjs(date).format(pattern);
}

/**
 * Check if date is within interval.
 * @param {Date} date
 * @param {Interval | false} interval
 * @returns {Boolean}
 */
export const isWithinInterval = (date, interval) =>
  !!interval && date >= interval.start && date <= interval.end;

/**
 * Check if intervalOne is contained within intervalTwo.
 * @param {Interval} intervalOne
 * @param {Interval} intervalTwo
 * @returns {Boolean}
 */
export const isIntervalWithinInterval = (intervalOne, intervalTwo) =>
  isWithinInterval(intervalOne.start, intervalTwo) &&
  isWithinInterval(intervalOne.end, intervalTwo);

/**
 * Get date interval from start of day to end of day
 * @param {Date} date
 * @returns {Interval}
 */
export const getDayAsInterval = (date) => ({
  start: dayjs(date).startOf('day').toDate(),
  end: dayjs(date).endOf('day').toDate(),
});

/**
 * Returns true if date is both a Date object and a valid Date.
 * @param {Date} date
 * @returns {Boolean}
 */
export const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

export const isValidDateString = (date) => {
  const dateValue = Date.parse(date);
  return isValidDate(new Date(dateValue));
};

/**
 * Attempts to parse isoString into a Date. If it cannot be successfully parsed,
 * returns fallbackDate instead.
 * @param {string} isoString
 * @param {Date} fallbackDate
 * @returns {Date}
 */
export const parseISOOrElse = (isoString, fallbackDate) => {
  if (isoString == null) return fallbackDate;
  const parsed = dayjs(isoString);
  return parsed.isValid() ? parsed.toDate() : fallbackDate;
};

/**
 * Returns true if interval is valid.
 * @param {Interval} interval
 * @returns {Boolean}
 */
export const isValidInterval = (interval) =>
  isValidDate(interval?.start) &&
  isValidDate(interval?.end) &&
  (dayjs(interval.start).isBefore(interval.end) || dayjs(interval.start).isSame(interval.end));

/**
 * Returns date if date is within interval -- otherwise returns the nearest endpoint of interval.
 * @param {Date} date
 * @param {Interval} interval
 * @returns {Boolean}
 */
export const clamp = (date, interval) => {
  if (dayjs(date).isBefore(interval.start)) return interval.start;
  if (dayjs(date).isAfter(interval.end)) return interval.end;
  return date;
};

/**
 * Returns a list of dates for each day in a specified interval
 * @param {Interval} interval
 * @returns {Date[]}
 */
export const eachDayOfInterval = (interval) => {
  let day = dayjs(interval.start).startOf('day');
  const dates = [];
  while (!day.isAfter(interval.end)) {
    dates.push(day.toDate());
    day = day.add(1, 'day');
  }
  return dates;
};

/**
 * Check if intervals overlap each other inclusively.
 * @param {Interval | false} intervalOne
 * @param {Interval | false} intervalTwo
 * @returns {Boolean}
 */
export const areIntervalsOverlapping = (intervalOne, intervalTwo) =>
  !!intervalOne &&
  !!intervalTwo &&
  (isWithinInterval(intervalOne.start, intervalTwo) ||
    isWithinInterval(intervalOne.end, intervalTwo) ||
    isWithinInterval(intervalTwo.start, intervalOne) ||
    isWithinInterval(intervalTwo.end, intervalOne));

export function isRangeInCurrentYear(range) {
  const today = dayjs();
  const start = dayjs(range?.[0], 'YYYY-MM-DD');
  const end = dayjs(range?.[1], 'YYYY-MM-DD');
  return today.isSame(start, 'year') && today.isSame(end, 'year');
}

export function bothRangesInCurrentYear(firstRange, secondRange) {
  return isRangeInCurrentYear(firstRange) && isRangeInCurrentYear(secondRange);
}

export function isSameRange(firstRange, secondRange) {
  const firstStartDate = dayjs(firstRange?.[0], 'YYYY-MM-DD');
  const firstEndDate = dayjs(firstRange?.[1], 'YYYY-MM-DD');
  const secondStartDate = dayjs(secondRange?.[0], 'YYYY-MM-DD');
  const secondEndDate = dayjs(secondRange?.[1], 'YYYY-MM-DD');
  return firstStartDate.isSame(secondStartDate, 'day') && firstEndDate.isSame(secondEndDate, 'day');
}

/**
 * Returns yesterday's date, computed fresh on each call.
 * Use this instead of caching `new Date()` in a computed or prop default
 * to avoid stale dates when a browser tab stays open overnight.
 */
export function getYesterday() {
  const today = new Date();
  return new Date(today.setDate(today.getDate() - 1));
}

export function isSameDateRange(firstRange, secondRange) {
  const firstStartDate = dayjs(firstRange?.[0]);
  const firstEndDate = dayjs(firstRange?.[1]);
  const secondStartDate = dayjs(secondRange?.[0]);
  const secondEndDate = dayjs(secondRange?.[1]);
  return firstStartDate.isSame(secondStartDate, 'day') && firstEndDate.isSame(secondEndDate, 'day');
}

/**
 * Get Interval from today looking back a number of days.
 * @param {Number} daysBack
 * @param {string} pattern dayjs format string, default 'YYYY-MM-DD'
 * @returns {Interval} but as string type in given format
 */
export function getDateRangeDaysBack(daysBack, pattern = 'YYYY-MM-DD') {
  const endDate = dayjs().format(pattern);
  const startDate = dayjs().subtract(daysBack, 'day').format(pattern);

  return [startDate, endDate];
}

/**
 * Find the max of a list of dates.
 * @param {Date} dates
 * @returns {Date}
 */
export const maxDates = (...dates) =>
  dates.reduce((maxSoFar, d) => (d > maxSoFar ? d : maxSoFar), DISTANT_PAST);

/**
 * Find the min of a list of dates.
 * @param {Date} dates
 * @returns {Date}
 */
export const minDates = (...dates) =>
  dates.reduce((minSoFar, d) => (d < minSoFar ? d : minSoFar), FAR_FUTURE);

/**
 * Find the interval consisting of dates which fall within both provided intervals.
 * @param {(Interval | false)[]} intervals
 * @returns {Interval | false}
 */
export const intersectIntervals = (...intervals) => {
  if (intervals.some((interval) => !interval)) {
    return false;
  }
  const start = maxDates(...intervals.map((interval) => interval.start));
  const end = minDates(...intervals.map((interval) => interval.end));
  return end < start ? false : { start, end };
};

/**
 * Find the duration of a date interval in milliseconds
 * @param {Interval | false} interval
 * @returns {Number}
 */
export const getIntervalMillisecondDuration = (interval) => {
  if (!interval) {
    return 0;
  }
  return interval.end - interval.start;
};

/**
 * Return an interval including all future dates.
 * @returns {Interval}
 */
export const getFutureAsInterval = () => ({ start: new Date(), end: FAR_FUTURE });

/**
 * Return true if the interval is open-ended.
 * @param {Interval | false} interval
 * @returns {Boolean}
 */
export const isAllTime = (interval) =>
  interval?.start === DISTANT_PAST && interval?.end === FAR_FUTURE;

/**
 * Return an array of date strings equal or between a start and end date
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {String} dateFmt
 * @param {String} timeUnit
 * @returns {String[]}
 */
export const generateStringsForDateRange = (startDate, endDate, dateFmt, timeUnit) => {
  const dateRange = [];
  let currentDate = dayjs(startDate);
  while (!currentDate.isAfter(endDate, timeUnit)) {
    dateRange.push(currentDate.format(dateFmt));
    currentDate = currentDate.add(1, timeUnit);
  }
  return dateRange;
};

/**
 * Parses a date string into a Date object
 * @param {String} value
 * @returns {Date}
 */
export function parseDate(value) {
  return dayjs(value, 'YYYY-MM-DD').toDate();
}

/**
 * Returns Date object
 * @param {Number} value
 * @returns {Date}
 */
export function daysAgo(value) {
  return dayjs().subtract(value, 'd').toDate();
}

/**
 * Return date interval with a specified amount of time subtracted from the specified date
 * @param {Date|String} endDate
 * @param {Number} subtractValue
 * @param {String} timeUnit
 * @param {import('@dashhudson/dashing-ui').DateService} [dateService]
 * @returns {Interval}
 */
export function getDateRange(endDate, subtractValue, unit = 'day', dateService) {
  const tz = dateService?.timezone;
  const djs = (date) => (tz ? dayjs.tz(date, tz) : dayjs(date));

  const endDayJs = unit === 'day' ? djs(endDate) : djs(endDate).startOf(unit).subtract(1, 'day');
  const end = djs(endDayJs.toDate()).endOf('day').toDate();

  const offset = unit === 'day' ? subtractValue : subtractValue - 1;
  const startDayJs =
    subtractValue > 1 || unit !== 'day' ? djs(end).subtract(offset, unit) : djs(end);
  const start = startDayJs.startOf(unit).toDate();
  return { start, end };
}

/**
 * Return date interval with a specified amount of time subtracted
 * @param {Number} subtractValue
 * @param {String} timeUnit
 * @param {import('@dashhudson/dashing-ui').DateService} [dateService]
 * @returns {Interval}
 */
export function lastDateRange(subtractValue = 1, unit = 'day', dateService) {
  const now = dateService ? dateService.now().toDate() : new Date();
  return getDateRange(now, subtractValue, unit, dateService);
}

/**
 * Get a date range starting from a given number of days ago to today
 * @param {Number} offsetDays
 * @param {import('@dashhudson/dashing-ui').DateService} [dateService]
 * @returns {DayjsInterval}
 */
export function getDateRangeOffsetFromToday(offsetDays, dateService) {
  const now = dateService ? dateService.now() : dayjs();
  return {
    start: now.subtract(offsetDays, 'd').startOf('day'),
    end: now.endOf('day'),
  };
}

/**
 * Returns a date range spanning from start of first day to end of last day
 * @param {Date|String} startDate
 * @param {Date|String} endDate
 * @param {import('@dashhudson/dashing-ui').DateService} [dateService]
 * @returns {DayjsInterval}
 */
export function getDateRangeFromDates(startDate, endDate, dateService) {
  const tz = dateService?.timezone;
  const djs = (date) => (tz ? dayjs.tz(date, tz) : dayjs(date));
  return {
    start: djs(startDate).startOf('day'),
    end: djs(endDate).endOf('day'),
  };
}

/**
 * Returns a date interval from a given start and end date.
 * @param {Date|String} startDate
 * @param {Date|String} endDate
 * @returns {Interval}
 */
export function getDateIntervalFromDates(startDate, endDate) {
  return {
    start: dayjs(startDate).startOf('day').toDate(),
    end: dayjs(endDate).endOf('day').toDate(),
  };
}

/**
 * Convert a dashing-ui `PresetType` to a `DateRangeModel`.
 * Useful when you want to set the modelValue of a dashing-ui DateRangeDropdown to a preset.
 * @param {PresetType} preset
 * @param {import('@dashhudson/dashing-ui').DateService} [dateService]
 * @returns {DateRangeModel}
 */
export function convertPresetToDateRangeModel(preset, dateService) {
  return { preset: preset.value, presetObject: preset, range: preset.range?.({ dateService }) };
}

/**
 * Find a dashing-ui `PresetType` by its value.
 * This function is copied from dashing-ui `date-ranges.ts` since it isn't currently exported.
 * @param {PresetType[]} presets
 * @param {string} presetValue
 * @returns {PresetType | undefined}
 */
export function findPresetObject(presets, presetValue) {
  return presets.find((preset) => preset.value === presetValue);
}

/**
 * Converts a Dayjs object to a JavaScript Date object by extracting its
 * individual date and time components (year, month, day, etc.) and passing
 * them to the JS Date constructor.
 *
 * This effectively drops any timezone information from the Dayjs object
 * and creates a Date object that represents the same local clock time
 * in the system's local timezone — not the same moment in time.
 *
 * Why this function is useful:
 * - If you use `.toDate()` on a timezone-aware Dayjs object (e.g., one created with `.tz()`),
 *   the resulting Date will represent the exact point in time (in UTC), which can shift the
 *   visible date/time when rendered in local time (e.g., in a date picker).
 * - This function avoids that by copying just the visible values (Y/M/D/H/m/s/ms) into a new
 *   Date object, so it preserves the user's intended clock time, even if the actual moment
 *   in time changes.
 *
 * @param {Dayjs} dayJsDate - The dayjs object to convert
 * @returns {Date} A JavaScript Date object with the same date/time values as the input
 *
 * @example
 * const dayjsDate = dayjs('2023-12-25 14:30:00').tz('Australia/Brisbane');
 * const jsDate = dayjsToDateIgnoreTimezone(dayjsDate);
 * // Returns a Date object representing December 25, 2023 at 2:30 PM, in local timezone
 */
export function dayjsToDateIgnoreTimezone(dayJsDate) {
  return new Date(
    dayJsDate.year(),
    dayJsDate.month(),
    dayJsDate.date(),
    dayJsDate.hour(),
    dayJsDate.minute(),
    dayJsDate.second(),
    dayJsDate.millisecond(),
  );
}

// ============================================================
// Wrapper exports (replacements for direct date-fns imports)
// ============================================================

/** Parse an ISO date string into a Date object. */
export function parseISODate(str) {
  return dayjs(str).toDate();
}

/** Format a Date as an ISO 8601 string (with local timezone offset). */
export function formatISODate(date) {
  return dayjs(date).format();
}

/** Returns true if d1 is before d2. */
export function isDateBefore(d1, d2) {
  return dayjs(d1).isBefore(d2);
}

/** Returns true if d1 is after d2. */
export function isDateAfter(d1, d2) {
  return dayjs(d1).isAfter(d2);
}

/** Returns true if d1 and d2 represent the same moment in time. */
export function isDateEqual(d1, d2) {
  return dayjs(d1).isSame(d2);
}

/** Returns true if d1 and d2 fall on the same calendar day. */
export function isSameDay(d1, d2) {
  if (d1 == null || d2 == null) return false;
  return dayjs(d1).isSame(d2, 'day');
}

/** Returns true if d1 and d2 fall in the same calendar month. */
export function isSameMonth(d1, d2) {
  if (d1 == null || d2 == null) return false;
  return dayjs(d1).isSame(d2, 'month');
}

/** Returns true if d1 and d2 fall in the same calendar year. */
export function isSameYear(d1, d2) {
  if (d1 == null || d2 == null) return false;
  return dayjs(d1).isSame(d2, 'year');
}

/** Returns the 4-digit year of a date. */
export function getYear(date) {
  return dayjs(date).year();
}

/** Returns the day of the month (1–31). */
export function getDate(date) {
  return dayjs(date).date();
}

/** Returns a Date set to the start of the given date's day (00:00:00.000). */
export function startOfDay(date) {
  if (date == null) return new Date(NaN);
  return dayjs(date).startOf('day').toDate();
}

/** Returns a Date set to the end of the given date's day (23:59:59.999). */
export function endOfDay(date) {
  if (date == null) return new Date(NaN);
  return dayjs(date).endOf('day').toDate();
}

/** Returns a Date set to the end of the given date's month. */
export function endOfMonth(date) {
  return dayjs(date).endOf('month').toDate();
}

/** Returns a Date set to the start of the given date's week. */
export function startOfWeek(date) {
  return dayjs(date).startOf('week').toDate();
}

/** Add n days to a Date, returning a new Date. */
export function addDays(date, n) {
  return dayjs(date).add(n, 'day').toDate();
}

/** Add n months to a Date, returning a new Date. */
export function addMonths(date, n) {
  return dayjs(date).add(n, 'month').toDate();
}

/** Add n weeks to a Date, returning a new Date. */
export function addWeeks(date, n) {
  return dayjs(date).add(n, 'week').toDate();
}

/** Add n hours to a Date, returning a new Date. */
export function addHours(date, n) {
  return dayjs(date).add(n, 'hour').toDate();
}

/** Add n minutes to a Date, returning a new Date. */
export function addMinutes(date, n) {
  return dayjs(date).add(n, 'minute').toDate();
}

/** Subtract n days from a Date, returning a new Date. */
export function subDays(date, n) {
  return dayjs(date).subtract(n, 'day').toDate();
}

/** Subtract n weeks from a Date, returning a new Date. */
export function subWeeks(date, n) {
  return dayjs(date).subtract(n, 'week').toDate();
}

/** Subtract n minutes from a Date, returning a new Date. */
export function subMinutes(date, n) {
  return dayjs(date).subtract(n, 'minute').toDate();
}

/** Get the difference between d1 and d2 in whole hours. */
export function differenceInHours(d1, d2) {
  return dayjs(d1).diff(d2, 'hour');
}

/** Get the difference between d1 and d2 in whole minutes. */
export function differenceInMinutes(d1, d2) {
  return dayjs(d1).diff(d2, 'minute');
}

/** Get the difference between d1 and d2 in calendar days (ignoring time). */
export function differenceInCalendarDays(d1, d2) {
  return dayjs(d1).startOf('day').diff(dayjs(d2).startOf('day'), 'day');
}

/** Get the difference between d1 and d2 in calendar months (ignoring day/time). */
export function differenceInCalendarMonths(d1, d2) {
  return dayjs(d1).startOf('month').diff(dayjs(d2).startOf('month'), 'month');
}
